/**
 * Núcleo del sistema de Skills (progresión estilo OSRS/RuneScape).
 *
 * TODAS las skills (combate, extracción, procesamiento) comparten la MISMA tabla
 * de experiencia: la curva canónica de OSRS (nivel 1 = 0 XP, nivel 99 =
 * 13.034.431 XP). Cada personaje lleva XP independiente por skill; el nivel se
 * DERIVA de la XP (no se persiste). Ver docs/SKILLS_SYSTEM.md.
 *
 * Fuente de verdad de los IDs/nombres de skill: `SKILL_DEFS`.
 */

import { WeaponSubtype } from '../types/item';
import type { SkillId, SkillDefinition, SkillCategory } from '../types/skills';

/** Nivel máximo de cualquier skill (igual que OSRS). */
export const SKILL_MAX_LEVEL = 99;

/**
 * Tabla de XP de OSRS. `SKILL_XP_TABLE[L]` = XP TOTAL acumulada necesaria para
 * ALCANZAR el nivel `L`. Índice 0 no se usa; nivel 1 = 0.
 *
 * Fórmula exacta de OSRS:
 *   total(L) = floor( (1/4) * Σ_{n=1}^{L-1} floor(n + 300 * 2^(n/7)) )
 *
 * Genera 1..99 (total(99) === 13_034_431). Misma curva para toda skill.
 */
export const SKILL_XP_TABLE: number[] = (function buildTable(): number[] {
  const table: number[] = [0, 0]; // índice 0 (unused) y nivel 1 = 0
  let points = 0;
  for (let level = 1; level < SKILL_MAX_LEVEL; level++) {
    points += Math.floor(level + 300 * Math.pow(2, level / 7));
    table[level + 1] = Math.floor(points / 4);
  }
  return table;
})();

/**
 * Nivel alcanzado con una cantidad de XP total. Clampea a [1, SKILL_MAX_LEVEL].
 */
export function getSkillLevelFromXp(xp: number): number {
  if (xp <= 0) return 1;
  let level = 1;
  while (level < SKILL_MAX_LEVEL && xp >= SKILL_XP_TABLE[level + 1]) {
    level++;
  }
  return level;
}

/** XP total requerida para alcanzar un nivel dado (1..99). */
export function getXpForSkillLevel(level: number): number {
  const clamped = Math.max(1, Math.min(SKILL_MAX_LEVEL, Math.floor(level)));
  return SKILL_XP_TABLE[clamped];
}

/**
 * Desglose de progreso dentro del nivel actual a partir de la XP total.
 *  - level:        nivel actual.
 *  - xpIntoLevel:  XP acumulada desde el inicio del nivel actual.
 *  - xpForSpan:    XP total que requiere el tramo nivel→nivel+1 (Infinity en 99).
 *  - xpToNext:     XP que falta para el próximo nivel (Infinity en 99).
 *  - totalForNext: XP total acumulada del próximo nivel (Infinity en 99).
 */
export function getXpToNextLevel(xp: number): {
  level: number;
  xpIntoLevel: number;
  xpForSpan: number;
  xpToNext: number;
  totalForNext: number;
} {
  const level = getSkillLevelFromXp(xp);
  const currentTotal = SKILL_XP_TABLE[level];
  if (level >= SKILL_MAX_LEVEL) {
    return {
      level,
      xpIntoLevel: Math.max(0, xp - currentTotal),
      xpForSpan: Infinity,
      xpToNext: Infinity,
      totalForNext: Infinity,
    };
  }
  const nextTotal = SKILL_XP_TABLE[level + 1];
  return {
    level,
    xpIntoLevel: xp - currentTotal,
    xpForSpan: nextTotal - currentTotal,
    xpToNext: nextTotal - xp,
    totalForNext: nextTotal,
  };
}

/**
 * Catálogo de skills. IDs en inglés, nombres en español.
 * `icon` es placeholder (`skill_<x>`) hasta tener arte real.
 */
// El campo `icon` es un nombre de ícono del atlas UI (ver client IconAtlases.ts).
// Combate reutiliza los íconos de arma existentes (ui_icons_2); el resto usa las
// entradas skill_* del atlas grande (ui_icons_3) + craft/arrow/boots reutilizados.
export const SKILL_DEFS: Record<SkillId, SkillDefinition> = {
  // ─── Combate (una por subtipo de arma + magia) ───────────────────────────
  skill_sword: { id: 'skill_sword', name: 'Espadas', category: 'combat', icon: 'sword', description: 'Dominio de espadas. Sube al golpear con una espada equipada.' },
  skill_dagger: { id: 'skill_dagger', name: 'Dagas', category: 'combat', icon: 'dagger', description: 'Dominio de dagas. Sube al golpear con una daga equipada.' },
  skill_axe: { id: 'skill_axe', name: 'Hachas', category: 'combat', icon: 'axe', description: 'Dominio de hachas. Sube al golpear con un hacha equipada.' },
  skill_mace: { id: 'skill_mace', name: 'Mazas', category: 'combat', icon: 'skill_mace', description: 'Dominio de mazas. Sube al golpear con una maza equipada.' },
  skill_staff: { id: 'skill_staff', name: 'Báculos', category: 'combat', icon: 'staff', description: 'Dominio de báculos. Sube al golpear con un báculo equipado.' },
  skill_wand: { id: 'skill_wand', name: 'Varas', category: 'combat', icon: 'wand', description: 'Dominio de varas. Sube al golpear con una vara equipada.' },
  skill_knuckles: { id: 'skill_knuckles', name: 'Nudilleras', category: 'combat', icon: 'skill_knuckles', description: 'Dominio del combate cuerpo a cuerpo con nudilleras.' },
  skill_bow: { id: 'skill_bow', name: 'Arcos', category: 'combat', icon: 'bow', description: 'Dominio de arcos. Sube al golpear con un arco equipado.' },
  skill_crossbow: { id: 'skill_crossbow', name: 'Ballestas', category: 'combat', icon: 'crossbow', description: 'Dominio de ballestas. Sube al golpear con una ballesta equipada.' },
  skill_throwable: { id: 'skill_throwable', name: 'Arrojadizas', category: 'combat', icon: 'throwable', description: 'Dominio de armas arrojadizas.' },
  skill_magic: { id: 'skill_magic', name: 'Magia', category: 'combat', icon: 'magic_damage', description: 'Dominio de la magia. Sube al lanzar hechizos ofensivos.' },
  // ─── Extracción ──────────────────────────────────────────────────────────
  skill_mining: { id: 'skill_mining', name: 'Minería', category: 'gathering', icon: 'skill_mining', description: 'Extracción de minerales de yacimientos. Necesita un pico.' },
  skill_woodcutting: { id: 'skill_woodcutting', name: 'Tala', category: 'gathering', icon: 'skill_woodcutting', description: 'Tala de árboles para obtener troncos. Necesita un hacha de leñador.' },
  skill_fishing: { id: 'skill_fishing', name: 'Pesca', category: 'gathering', icon: 'skill_fishing', description: 'Captura de peces en aguas del mundo. (En construcción.)' },
  skill_hunter: { id: 'skill_hunter', name: 'Caza', category: 'gathering', icon: 'skill_hunter', description: 'Rastreo y captura de criaturas salvajes. (En construcción.)' },
  skill_farming: { id: 'skill_farming', name: 'Agricultura', category: 'gathering', icon: 'skill_farming', description: 'Cultivo de plantas y cosecha. (En construcción.)' },
  // ─── Procesamiento ───────────────────────────────────────────────────────
  skill_smithing: { id: 'skill_smithing', name: 'Herrería', category: 'processing', icon: 'craft', description: 'Forja de armas y armaduras a partir de minerales. Requerida para equipar tiers altos.' },
  skill_carpentry: { id: 'skill_carpentry', name: 'Carpintería', category: 'processing', icon: 'skill_carpentry', description: 'Fabricación de objetos de madera a partir de troncos.' },
  skill_cooking: { id: 'skill_cooking', name: 'Cocina', category: 'processing', icon: 'skill_cooking', description: 'Preparación de alimentos que restauran y otorgan bonos. (En construcción.)' },
  skill_fletching: { id: 'skill_fletching', name: 'Flechería', category: 'processing', icon: 'arrow', description: 'Fabricación de arcos, ballestas y munición. (En construcción.)' },
  skill_crafting: { id: 'skill_crafting', name: 'Artesanía', category: 'processing', icon: 'skill_crafting', description: 'Confección de joyería, cuero y accesorios. (En construcción.)' },
  skill_tailoring: { id: 'skill_tailoring', name: 'Sastrería', category: 'processing', icon: 'skill_tailoring', description: 'Confección de túnicas y armaduras de tela a partir de tejidos. (En construcción.)' },
  skill_herblore: { id: 'skill_herblore', name: 'Herboristería', category: 'processing', icon: 'skill_herblore', description: 'Elaboración de pociones a partir de hierbas. (En construcción.)' },
  skill_construction: { id: 'skill_construction', name: 'Construcción', category: 'processing', icon: 'skill_construction', description: 'Edificación de estructuras y mobiliario. (En construcción.)' },
  // ─── Support (utilitarias) ───────────────────────────────────────────────
  skill_taming: { id: 'skill_taming', name: 'Doma', category: 'support', icon: 'skill_taming', description: 'Domesticación de animales para volverlos compañeros. (En construcción.)' },
  skill_prayer: { id: 'skill_prayer', name: 'Fe', category: 'support', icon: 'skill_prayer', description: 'Plegarias que otorgan bendiciones temporales. (En construcción.)' },
  skill_agility: { id: 'skill_agility', name: 'Agilidad', category: 'support', icon: 'boots', description: 'Movilidad, atajos y resistencia. (En construcción.)' },
  skill_thieving: { id: 'skill_thieving', name: 'Hurto', category: 'support', icon: 'skill_thieving', description: 'Robo a NPCs y cofres. (En construcción.)' },
};

/** Todas las skills en orden estable (combate → extracción → procesamiento). */
export const ALL_SKILL_IDS: SkillId[] = Object.keys(SKILL_DEFS) as SkillId[];

/**
 * Ancla dentro de la página de wiki de habilidades (`/habilidades`) para cada
 * skill. El slug matchea el que genera VitePress a partir del encabezado (acentos
 * y ñ removidos, minúsculas). Cada skill tiene su propia sección en la página.
 */
export const SKILL_WIKI_ANCHOR: Partial<Record<SkillId, string>> = {
  // Combate
  skill_sword: '#espadas',
  skill_dagger: '#dagas',
  skill_axe: '#hachas',
  skill_mace: '#mazas',
  skill_staff: '#baculos',
  skill_wand: '#varas',
  skill_knuckles: '#nudilleras',
  skill_bow: '#arcos',
  skill_crossbow: '#ballestas',
  skill_throwable: '#arrojadizas',
  skill_magic: '#magia',
  // Extracción
  skill_mining: '#mineria',
  skill_woodcutting: '#leneria',
  skill_fishing: '#pesca',
  skill_hunter: '#caza',
  skill_farming: '#agricultura',
  // Procesamiento
  skill_smithing: '#herreria',
  skill_carpentry: '#carpinteria',
  skill_cooking: '#cocina',
  skill_fletching: '#flecheria',
  skill_crafting: '#artesania',
  skill_tailoring: '#sastreria',
  skill_herblore: '#herboristeria',
  skill_construction: '#construccion',
  // Utilidad
  skill_taming: '#doma',
  skill_prayer: '#fe',
  skill_agility: '#agilidad',
  skill_thieving: '#hurto',
};

/** URL de wiki (página + ancla) de una skill. `base` = WIKI_URL del cliente. */
export function getSkillWikiUrl(base: string, id: SkillId): string {
  return `${base}/habilidades${SKILL_WIKI_ANCHOR[id] ?? ''}`;
}

/** Skills de una categoría. */
export function getSkillsByCategory(category: SkillCategory): SkillDefinition[] {
  return ALL_SKILL_IDS.map((id) => SKILL_DEFS[id]).filter((d) => d.category === category);
}

/** Metadata de una skill por id (o undefined). */
export function getSkillDef(id: string): SkillDefinition | undefined {
  return SKILL_DEFS[id as SkillId];
}

/** ¿Es `id` un SkillId válido? */
export function isValidSkillId(id: string): id is SkillId {
  return Object.prototype.hasOwnProperty.call(SKILL_DEFS, id);
}

/**
 * Mapea el subtipo de arma equipada → skill de combate que gana XP al golpear.
 * El daño mágico (spells) NO pasa por acá: usa `skill_magic` directamente.
 */
export const WEAPON_SUBTYPE_TO_SKILL: Record<WeaponSubtype, SkillId> = {
  [WeaponSubtype.SWORD]: 'skill_sword',
  [WeaponSubtype.DAGGER]: 'skill_dagger',
  [WeaponSubtype.AXE]: 'skill_axe',
  [WeaponSubtype.MACE]: 'skill_mace',
  [WeaponSubtype.STAFF]: 'skill_staff',
  [WeaponSubtype.WAND]: 'skill_wand',
  [WeaponSubtype.KNUCKLES]: 'skill_knuckles',
  [WeaponSubtype.BOW]: 'skill_bow',
  [WeaponSubtype.CROSSBOW]: 'skill_crossbow',
  [WeaponSubtype.THROWABLE]: 'skill_throwable',
};

/**
 * Skill de combate para un subtipo de arma (string). Devuelve undefined si el
 * subtipo no mapea (ej. arma sin subtipo → se ignora el award de XP).
 */
export function getCombatSkillForWeaponSubtype(subtype: string | undefined): SkillId | undefined {
  if (!subtype) return undefined;
  return WEAPON_SUBTYPE_TO_SKILL[subtype as WeaponSubtype];
}
